import type { StepConfig, WorkflowConfig } from '@reef/schema';

/**
 * A workflow drawn as the studio draws it: Start, one node per step, End.
 *
 * Vertical rather than left-to-right, because the thing a reader is checking is
 * the *order* — what runs before the gate, what runs after the approval — and a
 * column of six answers that at a glance where a horizontal graph makes them
 * count arrowheads.
 *
 * Child steps are drawn nested under their parent rather than as siblings.
 * Nesting is a security boundary in reef, not an indentation preference: a
 * child inherits every connection its parent mounted and can only ever narrow
 * the policy. Flattening the tree here would draw a step that looks like a peer
 * of the one whose credentials it is borrowing.
 */

export interface WorkflowGraphProps {
  workflow: WorkflowConfig;
  /** Step statuses from a run, when one is being shown. */
  statuses?: Record<string, string>;
  selectedStepId?: string | null;
  onSelectStep?: (stepId: string) => void;
}

/** Total steps including children — the number on the "N steps" chip. */
export function countSteps(steps: readonly StepConfig[]): number {
  return steps.reduce((total, step) => total + 1 + countSteps(step.childSteps ?? []), 0);
}

/**
 * What the node's dot means.
 *
 * A step with no run behind it is `idle` and reads as nothing at all — a grey
 * ring. Painting every node of an unstarted workflow in a colour would make a
 * document look like an incident.
 */
function tone(status: string | undefined): string {
  switch (status) {
    case 'completed':
      return 'ok';
    case 'failed':
      return 'danger';
    case 'running':
    case 'scheduled':
      return 'active';
    case 'awaiting_approval':
    case 'paused_budget':
    case 'paused_timeout':
      return 'warn';
    default:
      return 'idle';
  }
}

/** The one-word badge on a node that is doing something other than running. */
function badgeFor(step: StepConfig): { label: string; tone: string } | null {
  if (step.requiresApproval) return { label: 'approval', tone: 'warn' };
  if (step.watch) return { label: `watch · ${step.watch.cron}`, tone: 'active' };
  if (step.fanOut) return { label: `fan-out · ${step.fanOut.source}`, tone: 'active' };
  if (step.runtime === 'container') return { label: 'container', tone: 'muted' };
  return null;
}

function Node({
  step,
  depth,
  statuses,
  selectedStepId,
  onSelectStep,
}: {
  step: StepConfig;
  depth: number;
  statuses: Record<string, string>;
  selectedStepId: string | null | undefined;
  onSelectStep: ((stepId: string) => void) | undefined;
}) {
  const badge = badgeFor(step);
  const children = step.childSteps ?? [];

  return (
    <li className="graph-item" style={{ '--graph-depth': depth } as React.CSSProperties}>
      <button
        type="button"
        className="graph-node"
        aria-current={selectedStepId === step.id}
        onClick={() => onSelectStep?.(step.id)}
      >
        <span className={`graph-dot ${tone(statuses[step.id])}`} aria-hidden="true" />
        <span className="graph-node-label">{step.id}</span>
        {badge && <span className={`graph-badge ${badge.tone}`}>{badge.label}</span>}
      </button>

      {children.length > 0 && (
        <ul className="graph-children">
          {children.map((child) => (
            <Node
              key={child.id}
              step={child}
              depth={depth + 1}
              statuses={statuses}
              selectedStepId={selectedStepId}
              onSelectStep={onSelectStep}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function WorkflowGraph({
  workflow,
  statuses = {},
  selectedStepId,
  onSelectStep,
}: WorkflowGraphProps) {
  return (
    <div className="graph" role="group" aria-label={`${workflow.name} steps`}>
      <span className="graph-terminal">Start</span>
      <ul className="graph-list">
        {workflow.steps.map((step) => (
          <Node
            key={step.id}
            step={step}
            depth={0}
            statuses={statuses}
            selectedStepId={selectedStepId}
            onSelectStep={onSelectStep}
          />
        ))}
      </ul>
      <span className="graph-terminal">End</span>
    </div>
  );
}
